import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Badge } from "@radix-ui/themes";
import { ShoppingCart, Plus, Minus, X, ChevronUp, User, Phone, Utensils, Star, MessageSquare, ChevronRight } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../contexts/LanguageContext';
import apiClient from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

interface Review {
  _id: string;
  rating: number;
  comment: string;
  customerName: string;
  createdAt: string;
}

interface Size {
  name: string;
  nameAr?: string;
  price: number;
  originalPrice?: number;
  isDefault: boolean;
  isAvailable: boolean;
}

interface FoodItem {
  _id: string;
  name: string;
  nameAr?: string;
  description: string;
  descriptionAr?: string;
  price: number;
  originalPrice?: number;
  sizes?: Size[];
  category: string;
  image: string;
  isAvailable: boolean;
  rating?: {
    average: number;
    count: number;
  };
}

interface Table {
  _id: string;
  tableNumber: string;
  restaurant: {
    _id: string;
    name: string;
    nameAr?: string;
    logo: string;
    address: string;
  };
  capacity: number;
  status: string;
}

interface CartItem {
  foodItem: FoodItem;
  quantity: number;
  specialInstructions: string;
  selectedSize?: Size;
}

// Category translation key mapping
const categoryTranslationKeys: Record<string, string> = {
  'all': 'allItems',
  'fast-food': 'fastFood',
  'Fast food': 'fastFood',
  'fastfood': 'fastFood',
  'pizza': 'pizza',
  'Pizza': 'pizza',
  'asian': 'asian',
  'Asian': 'asian',
  'middle-eastern': 'middleEastern',
  'Middle-eastern': 'middleEastern',
  'Middle Eastern': 'middleEastern',
  'desserts': 'desserts',
  'Desserts': 'desserts',
  'dessert': 'desserts',
  'healthy': 'healthy',
  'Healthy': 'healthy',
  'coffee': 'coffee',
  'Coffee': 'coffee',
  'appetizer': 'appetizer',
  'Appetizer': 'appetizer',
  'main-dish': 'mainDish',
  'Main Dish': 'mainDish',
  'side-dish': 'sideDish',
  'Side Dish': 'sideDish',
  'sandwich': 'sandwich',
  'Sandwich': 'sandwich',
  'beverage': 'beverage',
  'Beverage': 'beverage',
  'drink': 'drink',
  'Drink': 'drink',
};

const TableOrderPage: React.FC = () => {
  const { qrCode } = useParams<{ qrCode: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, language, isRTL } = useLanguage();

  const [table, setTable] = useState<Table | null>(null);
  const [foodItems, setFoodItems] = useState<FoodItem[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tableOccupiedError, setTableOccupiedError] = useState(false);

  // Food item detail modal state
  const [selectedItem, setSelectedItem] = useState<FoodItem | null>(null);
  const [itemReviews, setItemReviews] = useState<Review[]>([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [showItemModal, setShowItemModal] = useState(false);
  const [selectedSize, setSelectedSize] = useState<Size | null>(null);

  // Get session token from localStorage
  const getSessionToken = () => {
    return qrCode ? localStorage.getItem(`table_session_${qrCode}`) : null;
  };

  // Save session token to localStorage
  const saveSessionToken = (token: string) => {
    if (qrCode && token) {
      localStorage.setItem(`table_session_${qrCode}`, token);
    }
  };

  useEffect(() => {
    if (qrCode) {
      fetchTableAndMenu();
      // Load saved customer info from localStorage
      const savedName = localStorage.getItem(`table_${qrCode}_name`);
      const savedPhone = localStorage.getItem(`table_${qrCode}_phone`);
      if (savedName) setCustomerName(savedName);
      if (savedPhone) setCustomerPhone(savedPhone);
    }
  }, [qrCode]);

  const fetchTableAndMenu = async () => {
    try {
      setLoading(true);
      setTableOccupiedError(false);

      const sessionToken = getSessionToken();
      const headers: Record<string, string> = {};
      if (sessionToken) {
        headers['x-table-session'] = sessionToken;
      }

      const response = await apiClient.get(`/table/${qrCode}`, { headers });
      const { table: tableData, foodItems: menuItems } = response.data;
      setTable(tableData);
      setFoodItems(menuItems);
    } catch (error: any) {
      console.error('Error fetching table data:', error);

      // Check if table is occupied by another customer
      if (error.response?.status === 403 && error.response?.data?.requiresSession) {
        setTableOccupiedError(true);
        toast({
          title: t("tableOccupied"),
          description: t("tableOccupiedByAnother"),
          variant: "destructive",
        });
      } else {
        toast({
          title: t("error"),
          description: t("unableToLoadTable"),
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const getCategoryLabel = (category: string): string => {
    const key = categoryTranslationKeys[category] || categoryTranslationKeys[category.toLowerCase()];
    return key ? t(key) : category;
  };

  const addToCart = (foodItem: FoodItem, size?: Size) => {
    // Determine the price based on size or base price
    const itemPrice = size ? size.price : foodItem.price;
    const sizeKey = size ? size.name : '';

    // For items with sizes, we need to track each size separately
    const existingItem = cart.find(item =>
      item.foodItem._id === foodItem._id &&
      (item.selectedSize?.name || '') === sizeKey
    );

    if (existingItem) {
      setCart(cart.map(item =>
        item.foodItem._id === foodItem._id && (item.selectedSize?.name || '') === sizeKey
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, { foodItem, quantity: 1, specialInstructions: '', selectedSize: size }]);
    }

    const itemName = language === 'ar' && foodItem.nameAr ? foodItem.nameAr : foodItem.name;
    const sizeName = size ? (language === 'ar' && size.nameAr ? size.nameAr : size.name) : '';

    toast({
      title: t("addedToCart"),
      description: sizeName ? `${itemName} (${sizeName})` : itemName,
    });
  };

  const updateQuantity = (foodItemId: string, quantity: number, sizeName?: string) => {
    if (quantity <= 0) {
      setCart(cart.filter(item =>
        !(item.foodItem._id === foodItemId && (item.selectedSize?.name || '') === (sizeName || ''))
      ));
    } else {
      setCart(cart.map(item =>
        item.foodItem._id === foodItemId && (item.selectedSize?.name || '') === (sizeName || '')
          ? { ...item, quantity }
          : item
      ));
    }
  };

  const updateSpecialInstructions = (foodItemId: string, instructions: string, sizeName?: string) => {
    setCart(cart.map(item =>
      item.foodItem._id === foodItemId && (item.selectedSize?.name || '') === (sizeName || '')
        ? { ...item, specialInstructions: instructions }
        : item
    ));
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => {
      const price = item.selectedSize ? item.selectedSize.price : item.foodItem.price;
      return total + (price * item.quantity);
    }, 0);
  };

  const getCartItemCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  const getFilteredItems = () => {
    if (selectedCategory === 'all') {
      return foodItems.filter(item => item.isAvailable);
    }
    return foodItems.filter(item => item.category === selectedCategory && item.isAvailable);
  };

  const getCategories = () => {
    const categories = Array.from(new Set(foodItems.map(item => item.category)));
    return ['all', ...categories];
  };

  const openItemDetails = async (item: FoodItem) => {
    setSelectedItem(item);
    setShowItemModal(true);
    setLoadingReviews(true);
    setItemReviews([]);

    // Set default size if item has sizes
    if (item.sizes && item.sizes.length > 0) {
      const defaultSize = item.sizes.find(s => s.isDefault && s.isAvailable) ||
        item.sizes.find(s => s.isAvailable) ||
        item.sizes[0];
      setSelectedSize(defaultSize);
    } else {
      setSelectedSize(null);
    }

    try {
      const response = await apiClient.get(`/table/food-item/${item._id}/reviews`);
      setItemReviews(response.data.reviews || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoadingReviews(false);
    }
  };

  const closeItemModal = () => {
    setShowItemModal(false);
    setSelectedItem(null);
    setItemReviews([]);
    setSelectedSize(null);
  };

  const formatReviewDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const placeOrder = async () => {
    if (!customerName.trim() || !customerPhone.trim()) {
      toast({
        title: t("missingInformation"),
        description: t("pleaseProvideNameAndPhone"),
        variant: "destructive",
      });
      return;
    }

    if (cart.length === 0) {
      toast({
        title: t("emptyCart"),
        description: t("pleaseAddItems"),
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      // Save customer info for future orders
      if (qrCode) {
        localStorage.setItem(`table_${qrCode}_name`, customerName);
        localStorage.setItem(`table_${qrCode}_phone`, customerPhone);
      }

      const orderData = {
        tableId: table?._id,
        restaurantId: table?.restaurant._id,
        customerName,
        customerPhone,
        items: cart.map(item => ({
          foodItemId: item.foodItem._id,
          quantity: item.quantity,
          specialInstructions: item.specialInstructions,
          size: item.selectedSize ? {
            name: item.selectedSize.name,
            nameAr: item.selectedSize.nameAr,
            price: item.selectedSize.price
          } : undefined,
          price: item.selectedSize ? item.selectedSize.price : item.foodItem.price,
        })),
        orderType: 'dine-in',
      };

      // Include session token in headers
      const sessionToken = getSessionToken();
      const headers: Record<string, string> = {};
      if (sessionToken) {
        headers['x-table-session'] = sessionToken;
      }

      const response = await apiClient.post('/table/orders', orderData, { headers });

      // Save session token if returned (first order)
      if (response.data.sessionToken) {
        saveSessionToken(response.data.sessionToken);
      }

      toast({
        title: t("orderPlacedSuccessfully"),
        description: `${t("orderNumber")}: ${response.data.order.orderNumber}`,
      });

      // Clear cart but keep customer info for additional orders
      setCart([]);
      setIsCartOpen(false);

      // Navigate to order status page
      navigate(`/order-status/${response.data.order.confirmationCode}?table=${qrCode}`);

    } catch (error) {
      console.error('Error placing order:', error);
      toast({
        title: t("error"),
        description: t("failedToPlaceOrder"),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">{t("loadingMenu")}</p>
        </div>
      </div>
    );
  }

  // Table is occupied by another customer
  if (tableOccupiedError) {
    return (
      <div className={`min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 max-w-sm w-full text-center">
          <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <Utensils className="w-8 h-8 text-orange-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t("tableOccupied")}</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{t("tableOccupiedByAnother")}</p>
          <Button onClick={() => navigate('/')} className="w-full">
            {t("goHome")}
          </Button>
        </div>
      </div>
    );
  }

  if (!table) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 max-w-sm w-full text-center">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t("invalidQRCode")}</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{t("tableNotAvailable")}</p>
          <Button onClick={() => navigate('/')} className="w-full">
            {t("goHome")}
          </Button>
        </div>
      </div>
    );
  }

  const restaurantName = language === 'ar' && table.restaurant.nameAr ? table.restaurant.nameAr : table.restaurant.name;

  return (
    <div className={`min-h-screen bg-gray-50 dark:bg-gray-900 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 z-30 bg-white dark:bg-gray-800 shadow-sm">
        <div className="px-4 py-3">
          <div className="flex items-center gap-3">
            {table.restaurant.logo ? (
              <img
                src={table.restaurant.logo}
                alt={restaurantName}
                className="w-10 h-10 rounded-xl object-cover border border-gray-200 dark:border-gray-600"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            ) : (
              <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <Utensils className="w-5 h-5 text-red-600" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-bold text-gray-900 dark:text-white truncate">
                {restaurantName}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("table")} {table.tableNumber}
              </p>
            </div>
            <Badge color={table.status === 'available' ? 'green' : 'red'} size="2">
              {table.status === 'available' ? t("available") : t("occupied")}
            </Badge>
          </div>
        </div>
      </header>

      {/* Category Tabs - Fixed below header */}
      <div className="fixed top-[60px] left-0 right-0 z-20 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="overflow-x-auto scrollbar-hide">
          <div className="flex gap-2 px-4 py-3">
            {getCategories().map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${selectedCategory === category
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
              >
                {getCategoryLabel(category)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content - Menu Items */}
      <main className="pt-[120px] pb-24 px-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {getFilteredItems().map(item => {
            const itemName = language === 'ar' && item.nameAr ? item.nameAr : item.name;
            const itemDesc = language === 'ar' && item.descriptionAr ? item.descriptionAr : item.description;
            const cartItems = cart.filter(c => c.foodItem._id === item._id);
            const totalInCart = cartItems.reduce((sum, c) => sum + c.quantity, 0);

            // Calculate price display
            const hasSizes = item.sizes && item.sizes.length > 0;
            const availableSizes = hasSizes ? item.sizes!.filter(s => s.isAvailable) : [];
            let priceDisplay = `SDG ${item.price.toFixed(2)}`;
            if (availableSizes.length > 0) {
              const prices = availableSizes.map(s => s.price).sort((a, b) => a - b);
              const minPrice = prices[0];
              const maxPrice = prices[prices.length - 1];
              priceDisplay = minPrice === maxPrice
                ? `SDG ${minPrice.toFixed(2)}`
                : `SDG ${minPrice.toFixed(2)} - $${maxPrice.toFixed(2)}`;
            }

            return (
              <div
                key={item._id}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm overflow-hidden border border-gray-100 dark:border-gray-700"
              >
                {item.image && (
                  <div
                    className="relative h-40 bg-gray-100 dark:bg-gray-700 cursor-pointer"
                    onClick={() => openItemDetails(item)}
                  >
                    <img
                      src={item.image}
                      alt={itemName}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = 'https://via.placeholder.com/300x200?text=No+Image';
                      }}
                    />
                    <div className={`absolute top-2 ${isRTL ? 'right-2' : 'left-2'}`}>
                      <span className="px-2 py-1 text-xs font-medium bg-white/90 dark:bg-gray-800/90 text-gray-700 dark:text-gray-300 rounded-full">
                        {getCategoryLabel(item.category)}
                      </span>
                    </div>
                    {/* Rating badge */}
                    {item.rating && item.rating.count > 0 && (
                      <div className={`absolute top-2 ${isRTL ? 'left-2' : 'right-2'}`}>
                        <span className="px-2 py-1 text-xs font-medium bg-yellow-400 text-yellow-900 rounded-full flex items-center gap-1">
                          <Star className="w-3 h-3 fill-current" />
                          {item.rating.average.toFixed(1)}
                        </span>
                      </div>
                    )}
                    {/* View details indicator */}
                    <div className="absolute bottom-2 right-2 left-2 flex justify-center">
                      <span className="px-3 py-1 text-xs font-medium bg-black/50 text-white rounded-full flex items-center gap-1">
                        {hasSizes ? t("selectSize") : t("tapToViewDetails")}
                        <ChevronRight className="w-3 h-3" />
                      </span>
                    </div>
                  </div>
                )}
                <div className="p-4">
                  <div className="flex justify-between items-start gap-2 mb-1">
                    <h3
                      className="font-semibold text-gray-900 dark:text-white line-clamp-1 cursor-pointer hover:text-red-600"
                      onClick={() => openItemDetails(item)}
                    >
                      {itemName}
                    </h3>
                    <span className="font-bold text-red-600 whitespace-nowrap text-sm">{priceDisplay}</span>
                  </div>
                  {/* Rating and reviews count */}
                  {item.rating && item.rating.count > 0 && (
                    <button
                      onClick={() => openItemDetails(item)}
                      className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 mb-2 hover:text-yellow-500"
                    >
                      <Star className="w-4 h-4 text-yellow-400 fill-current" />
                      <span>{item.rating.average.toFixed(1)}</span>
                      <span>({item.rating.count} {t("reviews")})</span>
                    </button>
                  )}
                  {itemDesc && (
                    <p className="text-gray-500 dark:text-gray-400 text-sm mb-4 line-clamp-2">{itemDesc}</p>
                  )}

                  {totalInCart > 0 ? (
                    <div className="flex items-center justify-between bg-gray-100 dark:bg-gray-700 rounded-xl p-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 px-2">
                        {totalInCart} {t("inCart")}
                      </span>
                      <Button
                        onClick={() => openItemDetails(item)}
                        size="sm"
                        className="bg-red-600 hover:bg-red-700 text-white rounded-lg"
                      >
                        {hasSizes ? t("addMore") : <Plus className="w-4 h-4" />}
                      </Button>
                    </div>
                  ) : hasSizes ? (
                    <Button
                      onClick={() => openItemDetails(item)}
                      className="w-full bg-red-600 hover:bg-red-700 text-white rounded-xl"
                      disabled={!item.isAvailable}
                    >
                      {t("selectSize")}
                    </Button>
                  ) : (
                    <Button
                      onClick={() => addToCart(item)}
                      className="w-full bg-red-600 hover:bg-red-700 text-white rounded-xl"
                      disabled={!item.isAvailable}
                    >
                      <Plus className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                      {t("addToOrder")}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {getFilteredItems().length === 0 && (
          <div className="text-center py-12">
            <Utensils className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">{t("noItemsInCategory")}</p>
          </div>
        )}
      </main>

      {/* Floating Cart Button */}
      {cart.length > 0 && !isCartOpen && (
        <button
          onClick={() => setIsCartOpen(true)}
          className="fixed bottom-6 left-4 right-4 z-40 bg-red-600 text-white rounded-2xl shadow-lg p-4 flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <div className="relative">
              <ShoppingCart className="w-6 h-6" />
              <span className="absolute -top-2 -right-2 w-5 h-5 bg-white text-red-600 text-xs font-bold rounded-full flex items-center justify-center">
                {getCartItemCount()}
              </span>
            </div>
            <span className="font-semibold">{t("viewCart")}</span>
          </div>
          <span className="font-bold text-lg">SDG {getCartTotal().toFixed(2)}</span>
        </button>
      )}

      {/* Bottom Drawer Cart */}
      {isCartOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setIsCartOpen(false)}
          />

          {/* Drawer */}
          <div className="fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-gray-800 rounded-t-3xl shadow-2xl max-h-[85vh] flex flex-col animate-slide-up">
            {/* Drawer Handle */}
            <div className="flex justify-center py-3">
              <div className="w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full" />
            </div>

            {/* Drawer Header */}
            <div className="flex items-center justify-between px-6 pb-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-red-600" />
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">{t("yourOrder")}</h2>
                <Badge color="red" size="1">{getCartItemCount()}</Badge>
              </div>
              <button
                onClick={() => setIsCartOpen(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
              >
                <ChevronUp className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Cart Items - Scrollable */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {/* Customer Info */}
              <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-2xl">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-4">{t("yourInformation")}</h3>
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm text-gray-600 dark:text-gray-400">{t("name")}</Label>
                    <div className="relative mt-1">
                      <User className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 ${isRTL ? 'right-3' : 'left-3'}`} />
                      <Input
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        placeholder={t("enterYourName")}
                        className={`${isRTL ? 'pr-10' : 'pl-10'} rounded-xl`}
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm text-gray-600 dark:text-gray-400">{t("phoneNumber")}</Label>
                    <div className="relative mt-1">
                      <Phone className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 ${isRTL ? 'right-3' : 'left-3'}`} />
                      <Input
                        value={customerPhone}
                        onChange={(e) => setCustomerPhone(e.target.value)}
                        placeholder={t("enterPhoneNumber")}
                        className={`${isRTL ? 'pr-10' : 'pl-10'} rounded-xl`}
                        dir="ltr"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Cart Items */}
              <div className="space-y-4">
                {cart.map((item, index) => {
                  const itemName = language === 'ar' && item.foodItem.nameAr ? item.foodItem.nameAr : item.foodItem.name;
                  const sizeName = item.selectedSize
                    ? (language === 'ar' && item.selectedSize.nameAr ? item.selectedSize.nameAr : item.selectedSize.name)
                    : '';
                  const itemPrice = item.selectedSize ? item.selectedSize.price : item.foodItem.price;
                  const cartKey = `${item.foodItem._id}-${item.selectedSize?.name || 'default'}-${index}`;

                  return (
                    <div key={cartKey} className="bg-gray-50 dark:bg-gray-700/50 rounded-2xl p-4">
                      <div className="flex items-start gap-3">
                        {item.foodItem.image && (
                          <img
                            src={item.foodItem.image}
                            alt={itemName}
                            className="w-16 h-16 rounded-xl object-cover"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 dark:text-white">{itemName}</h4>
                          {sizeName && (
                            <p className="text-xs text-red-600 font-medium">{sizeName}</p>
                          )}
                          <p className="text-sm text-gray-500 dark:text-gray-400">SDG {itemPrice.toFixed(2)}</p>
                        </div>
                        <div className="flex items-center gap-2 bg-white dark:bg-gray-600 rounded-xl p-1">
                          <button
                            onClick={() => updateQuantity(item.foodItem._id, item.quantity - 1, item.selectedSize?.name)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-500"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="w-6 text-center font-semibold">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.foodItem._id, item.quantity + 1, item.selectedSize?.name)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-500"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="mt-3">
                        <Input
                          value={item.specialInstructions}
                          onChange={(e) => updateSpecialInstructions(item.foodItem._id, e.target.value, item.selectedSize?.name)}
                          placeholder={t("specialInstructions")}
                          className="text-sm rounded-xl bg-white dark:bg-gray-600"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Drawer Footer - Fixed */}
            <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 bg-white dark:bg-gray-800">
              <div className="flex justify-between items-center mb-4">
                <span className="text-gray-600 dark:text-gray-400">{t("total")}</span>
                <span className="text-2xl font-bold text-gray-900 dark:text-white">SDG {getCartTotal().toFixed(2)}</span>
              </div>
              <Button
                onClick={placeOrder}
                disabled={cart.length === 0 || !customerName.trim() || !customerPhone.trim() || isSubmitting}
                className="w-full bg-red-600 hover:bg-red-700 text-white rounded-xl h-14 text-lg font-semibold"
              >
                {isSubmitting ? t("placingOrder") : t("placeOrder")}
              </Button>
            </div>
          </div>
        </>
      )
      }

      {/* Custom Styles */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
        .line-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>

      {/* Food Item Detail Modal */}
      {
        showItemModal && selectedItem && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 z-50"
              onClick={closeItemModal}
            />

            {/* Modal */}
            <div className="fixed inset-x-4 top-[10%] bottom-[10%] z-50 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl flex flex-col overflow-hidden max-w-lg mx-auto">
              {/* Modal Header with Image */}
              <div className="relative h-48 bg-gray-100 dark:bg-gray-700 flex-shrink-0">
                {selectedItem.image && (
                  <img
                    src={selectedItem.image}
                    alt={language === 'ar' && selectedItem.nameAr ? selectedItem.nameAr : selectedItem.name}
                    className="w-full h-full object-cover"
                  />
                )}
                <button
                  onClick={closeItemModal}
                  className="absolute top-4 right-4 w-10 h-10 bg-white/90 dark:bg-gray-800/90 rounded-full flex items-center justify-center shadow-lg"
                >
                  <X className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                </button>
                {/* Rating badge */}
                {selectedItem.rating && selectedItem.rating.count > 0 && (
                  <div className="absolute bottom-4 left-4">
                    <span className="px-3 py-1.5 text-sm font-medium bg-yellow-400 text-yellow-900 rounded-full flex items-center gap-1.5 shadow-lg">
                      <Star className="w-4 h-4 fill-current" />
                      {selectedItem.rating.average.toFixed(1)}
                      <span className="text-yellow-700">({selectedItem.rating.count})</span>
                    </span>
                  </div>
                )}
              </div>

              {/* Modal Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {/* Item Info */}
                <div className="mb-6">
                  <div className="flex justify-between items-start gap-2 mb-2">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                      {language === 'ar' && selectedItem.nameAr ? selectedItem.nameAr : selectedItem.name}
                    </h2>
                    <span className="text-xl font-bold text-red-600">SDG {(selectedSize ? selectedSize.price : selectedItem.price).toFixed(2)}
                    </span>
                  </div>
                  <span className="inline-block px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full mb-3">
                    {getCategoryLabel(selectedItem.category)}
                  </span>
                  <p className="text-gray-600 dark:text-gray-400">
                    {language === 'ar' && selectedItem.descriptionAr ? selectedItem.descriptionAr : selectedItem.description}
                  </p>
                </div>

                {/* Size Selection */}
                {selectedItem.sizes && selectedItem.sizes.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-3">{t("selectSize")}</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {selectedItem.sizes.filter(s => s.isAvailable).map((size) => {
                        const sizeName = language === 'ar' && size.nameAr ? size.nameAr : size.name;
                        const isSelected = selectedSize?.name === size.name;
                        return (
                          <button
                            key={size.name}
                            onClick={() => setSelectedSize(size)}
                            className={`p-3 rounded-xl border-2 transition-all ${isSelected
                              ? 'border-red-600 bg-red-50 dark:bg-red-900/20'
                              : 'border-gray-200 dark:border-gray-600 hover:border-red-300'
                              }`}
                          >
                            <div className="text-sm font-medium text-gray-900 dark:text-white">{sizeName}</div>
                            <div className={`text-sm ${isSelected ? 'text-red-600' : 'text-gray-500 dark:text-gray-400'}`}>SDG {size.price.toFixed(2)}
                              {size.originalPrice && size.originalPrice > size.price && (
                                <span className="ml-1 line-through text-gray-400 text-xs">SDG {size.originalPrice.toFixed(2)}
                                </span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Reviews Section */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <MessageSquare className="w-5 h-5 text-gray-500" />
                    <h3 className="font-semibold text-gray-900 dark:text-white">{t("customerReviews")}</h3>
                    {selectedItem.rating && (
                      <span className="text-sm text-gray-500">({selectedItem.rating.count} {t("reviews")})</span>
                    )}
                  </div>

                  {loadingReviews ? (
                    <div className="flex justify-center py-8">
                      <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : itemReviews.length > 0 ? (
                    <div className="space-y-4">
                      {itemReviews.map((review, index) => (
                        <div key={review._id || index} className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                                <User className="w-4 h-4 text-red-600" />
                              </div>
                              <span className="font-medium text-gray-900 dark:text-white">{review.customerName}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star
                                  key={star}
                                  className={`w-4 h-4 ${star <= review.rating ? 'text-yellow-400 fill-current' : 'text-gray-300 dark:text-gray-600'}`}
                                />
                              ))}
                            </div>
                          </div>
                          {review.comment && (
                            <p className="text-gray-600 dark:text-gray-400 text-sm mb-2">{review.comment}</p>
                          )}
                          <p className="text-xs text-gray-400 dark:text-gray-500">{formatReviewDate(review.createdAt)}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Star className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                      <p className="text-gray-500 dark:text-gray-400">{t("noReviewsYet")}</p>
                      <p className="text-sm text-gray-400 dark:text-gray-500">{t("beFirstToReview")}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-800 flex-shrink-0">
                <Button
                  onClick={() => {
                    addToCart(selectedItem, selectedSize || undefined);
                    closeItemModal();
                  }}
                  className="w-full bg-red-600 hover:bg-red-700 text-white rounded-xl h-14 text-lg font-semibold"
                  disabled={!selectedItem.isAvailable || (selectedItem.sizes && selectedItem.sizes.length > 0 && !selectedSize)}
                >
                  <Plus className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {t("addToOrder")} - ${(selectedSize ? selectedSize.price : selectedItem.price).toFixed(2)}
                </Button>
              </div>
            </div>
          </>
        )
      }
    </div >
  );
};

export default TableOrderPage;